import Cookies from "js-cookie";

export interface StoredTrackingParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  _fbp: string | null;
  _fbc: string | null;
}

const TRACKING_PARAMS_STORAGE_KEY = "utmParams";

export function buildFbcFromFbclid(
  fbclid: string,
  creationTime = Date.now()
): string {
  return `fb.2.${creationTime}.${fbclid}`;
}

export function getFbcCreationTime(fbc: string | null | undefined): number | null {
  if (!fbc) return null;

  const match = fbc.match(/^fb\.\d+\.(\d+)\..+$/);
  if (!match) return null;

  const creationTime = Number(match[1]);
  return Number.isFinite(creationTime) ? creationTime : null;
}

function getNewerFbc(
  ...fbcCandidates: Array<string | null | undefined>
): string | null {
  return fbcCandidates.reduce<string | null>((newerFbc, candidateFbc) => {
    if (!candidateFbc) return newerFbc;
    if (!newerFbc) return candidateFbc;

    const candidateCreationTime = getFbcCreationTime(candidateFbc);
    const newerCreationTime = getFbcCreationTime(newerFbc);

    if (candidateCreationTime === null && newerCreationTime !== null) {
      return newerFbc;
    }

    if (candidateCreationTime !== null && newerCreationTime === null) {
      return candidateFbc;
    }

    if (candidateCreationTime === null || newerCreationTime === null) {
      return candidateFbc;
    }

    return candidateCreationTime >= newerCreationTime ? candidateFbc : newerFbc;
  }, null);
}

export function getStoredTrackingParams(
  storage: Pick<Storage, "getItem"> = localStorage
): Partial<StoredTrackingParams> {
  try {
    const stored = storage.getItem(TRACKING_PARAMS_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as Partial<StoredTrackingParams>;
  } catch {
    return {};
  }
}

export function getCapturedTrackingParams(
  search: string,
  options: {
    now?: () => number;
    storage?: Pick<Storage, "getItem">;
  } = {}
): StoredTrackingParams | null {
  const urlParams = new URLSearchParams(search);
  const fbclid = urlParams.get("fbclid");
  const incomingParams = {
    utm_source: urlParams.get("utm_source"),
    utm_medium: urlParams.get("utm_medium"),
    utm_campaign: urlParams.get("utm_campaign"),
    utm_term: urlParams.get("utm_term"),
    utm_content: urlParams.get("utm_content"),
    _fbp: urlParams.get("_fbp") ?? urlParams.get("fbp"),
    _fbc: urlParams.get("_fbc") ?? urlParams.get("fbc"),
  };

  if (!fbclid && !Object.values(incomingParams).some(Boolean)) {
    return null;
  }

  const stored = getStoredTrackingParams(options.storage);
  const fbclidFbc = fbclid
    ? buildFbcFromFbclid(fbclid, options.now ? options.now() : Date.now())
    : null;
  const fbc = getNewerFbc(stored._fbc, incomingParams._fbc, fbclidFbc);

  return {
    utm_source: incomingParams.utm_source ?? stored.utm_source ?? null,
    utm_medium: incomingParams.utm_medium ?? stored.utm_medium ?? null,
    utm_campaign: incomingParams.utm_campaign ?? stored.utm_campaign ?? null,
    utm_term: incomingParams.utm_term ?? stored.utm_term ?? null,
    utm_content: incomingParams.utm_content ?? stored.utm_content ?? null,
    _fbp: incomingParams._fbp ?? stored._fbp ?? null,
    _fbc: fbc,
  };
}

export function persistTrackingParams(
  params: StoredTrackingParams,
  domain: string
): void {
  localStorage.setItem(TRACKING_PARAMS_STORAGE_KEY, JSON.stringify(params));

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      Cookies.set(key, value, {
        expires: 30,
        sameSite: "lax",
        domain,
      });
    }
  });
}
